import { ApplicationRef, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { ADMIN_OR_ABOVE_ROLES } from '@shared';
import { Socket, SOCKET_CONFIG_TOKEN, SocketIoConfig } from 'ngx-socket-io';
import * as fromAdmin from './admin';
import { adminGeneralCanDeactivateGuard } from './admin/general/admin-general.can-deactivate.guard';
import { adminGroupsCanDeactivateGuard } from './admin/groups/admin-groups.can-deactivate.guard';
import { adminManagedSkillsCanActivateGuard } from './admin/managed-skills/admin-managed-skills.can-activate.guard';
import { adminTeammatesCanDeactivateGuard } from './admin/teammates/admin-teammates.can-deactivate.guard';
import * as fromAuth from './auth';
import * as fromChats from './chats';
import * as fromChatsAssistants from './chats/assistants';
import { assistantsCanDeactivateGuard } from './chats/assistants/assistants.can-deactivate.guard';
import { provideSpeechToText } from './chats/conversation';
import { conversationCanActivateGuard } from './chats/conversation/conversation.can-activate.guard';
import * as fromTeammates from './chats/teammates';
import * as fromConnectors from './connectors';
import * as fromLayout from './layout';
import { NotFoundPage } from './not-found.page';

export const appRoutes: Route[] = [
  {
    path: 'login',
    loadComponent: () => import('./auth/login.page').then((m) => m.LoginPage),
    canActivate: [fromAuth.loginGuard],
  },
  {
    path: 'playground',
    loadComponent: () =>
      import('./playground.page').then((m) => m.PlaygroundPage),
  },
  {
    path: 'chats',
    canActivate: [fromAuth.authGuard],
    canActivateChild: [fromAuth.authGuard],
    providers: [
      provideState(fromLayout.layoutFeature),
      // The connectors modal is hosted by the chats shell, so its state belongs to this branch.
      provideState(fromConnectors.connectorsFeature),
      provideEffects(fromConnectors.ConnectorsEffects),
    ],
    loadComponent: () =>
      import('./layout/layout.page').then((m) => m.LayoutPage),
    children: [
      {
        path: '',
        providers: [
          fromChats.ChatsSocket,
          {
            provide: SOCKET_CONFIG_TOKEN,
            useValue: fromChats.chatsSocketConfig,
          },
          {
            provide: Socket,
            useFactory: (
              config: SocketIoConfig,
              appRef: ApplicationRef,
              ngZone: NgZone,
            ) => {
              let socket!: Socket;
              ngZone.runOutsideAngular(() => {
                socket = new Socket(config, appRef);
              });
              return socket;
            },
            deps: [SOCKET_CONFIG_TOKEN, ApplicationRef, NgZone],
          },
          provideState(fromChats.chatsFeature),
          provideEffects(
            fromChats.ChatsEffects,
            fromChats.ChatsRouterEffects,
            fromChats.ChatsAutoSendEffects,
          ),
          provideState(fromChatsAssistants.assistantsFeature),
          provideEffects(fromChatsAssistants.AssistantsEffects),
          provideState(fromTeammates.teammatesFeature),
          provideEffects(fromTeammates.TeammatesEffects),
          provideSpeechToText(),
        ],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        resolve: {
          chats: fromChats.chatsResolver,
          assistants: fromChatsAssistants.assistantsResolver,
          teammates: fromTeammates.teammatesResolver,
        },
        loadComponent: () =>
          import('./chats/chats.page').then((m) => m.ChatsPage),
        children: [
          {
            path: '',
            redirectTo: 'teammates',
            pathMatch: 'full',
          },
          {
            matcher: (segments) => {
              if (
                segments.length === 1 &&
                segments[0].path === 'conversation'
              ) {
                /**
                 * Ex.: /chats/conversation
                 */
                return { consumed: segments };
              }
              if (
                segments.length === 2 &&
                segments[0].path === 'conversation'
              ) {
                /**
                 * Ex.: /chats/conversation/<uuid>
                 * Ex.: /chats/conversation/<uuid>?assistantUuid=<skill uuid>
                 */
                return { consumed: segments, posParams: { id: segments[1] } };
              }
              return null;
            },
            loadComponent: () =>
              import('./chats/conversation/conversation.page').then(
                (m) => m.ConversationPage,
              ),
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            canActivate: [conversationCanActivateGuard],
            pathMatch: 'full',
          },
          {
            path: 'assistants',
            title: 'ASSISTANTS.TITLE',
            loadComponent: () =>
              import('./chats/assistants/assistants.page').then(
                (m) => m.AssistantsPage,
              ),
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            // Prompt on navigate-away while the skill builder modal holds
            // unsaved work (SB-3325 PR-4, Stage B).
            canDeactivate: [assistantsCanDeactivateGuard],
            resolve: {
              tab: (route: ActivatedRouteSnapshot) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                route.queryParams['tab'],
            },
          },
          {
            path: 'teammates',
            title: 'PAGE_TITLE.TEAMMATES',
            loadComponent: () =>
              import('./chats/teammates/teammates-landing.page').then(
                (m) => m.TeammatesLandingPage,
              ),
          },
          {
            path: 'teammates/:slug',
            title: 'PAGE_TITLE.TEAMMATES',
            loadComponent: () =>
              import('./chats/teammates/teammate.page').then(
                (m) => m.TeammatePage,
              ),
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            // The skill builder is reachable from this page too since
            // SB-3650 / T5, so it needs the same unsaved-work prompt the
            // assistants route has carried since SB-3325 PR-4.
            canDeactivate: [assistantsCanDeactivateGuard],
            resolve: (route: ActivatedRouteSnapshot) => ({
              slug: route.params['slug'] as string,
            }),
          },
        ],
      },
    ],
  },
  {
    path: 'admin',
    // Admin is reachable on every viewport — the shell renders as-is on small
    // screens rather than redirecting away or blocking with a notice.
    canActivate: [
      fromAuth.authGuard,
      fromAuth.authRoleGuard([...ADMIN_OR_ABOVE_ROLES]),
    ],
    canActivateChild: [
      fromAuth.authGuard,
      fromAuth.authRoleGuard([...ADMIN_OR_ABOVE_ROLES]),
    ],
    loadComponent: () => import('./admin/admin.page').then((m) => m.AdminPage),
    children: [
      { path: '', redirectTo: 'general', pathMatch: 'full' },
      {
        // Loaded and saved through /v1/organization-settings (admin only).
        // No store slice: the page holds its own state.
        path: 'general',
        title: 'ADMIN.NAV.GENERAL',
        loadComponent: () =>
          import('./admin/general/admin-general.page').then(
            (m) => m.AdminGeneralPage,
          ),
        canDeactivate: [adminGeneralCanDeactivateGuard],
      },
      {
        path: 'users',
        title: 'ADMIN.NAV.USERS',
        providers: [
          provideState(fromAdmin.adminUsersFeature),
          provideEffects(fromAdmin.AdminUsersEffects),
        ],
        loadComponent: () =>
          import('./admin/users/admin-users.page').then(
            (m) => m.AdminUsersPage,
          ),
      },
      {
        path: 'groups',
        title: 'ADMIN.NAV.GROUPS',
        // Both slices: the form modal's member multi-select reads the users
        // slice, and each /admin/* child route is independently state-scoped.
        providers: [
          provideState(fromAdmin.adminGroupsFeature),
          provideEffects(fromAdmin.AdminGroupsEffects),
          provideState(fromAdmin.adminUsersFeature),
          provideEffects(fromAdmin.AdminUsersEffects),
        ],
        loadComponent: () =>
          import('./admin/groups/admin-groups.page').then(
            (m) => m.AdminGroupsPage,
          ),
        canDeactivate: [adminGroupsCanDeactivateGuard],
      },
      {
        path: 'teammates',
        title: 'ADMIN.NAV.TEAMMATES',
        providers: [
          provideState(fromAdmin.adminTeammatesFeature),
          provideEffects(fromAdmin.AdminTeammatesEffects),
        ],
        loadComponent: () =>
          import('./admin/teammates/admin-teammates.page').then(
            (m) => m.AdminTeammatesPage,
          ),
        canDeactivate: [adminTeammatesCanDeactivateGuard],
      },
      {
        // The URL says skills to match the analytics section; the code says
        // managed-skills, after the API controller. Not the API's
        // admin/skills, the Teammate Builder search.
        path: 'skills',
        title: 'ADMIN.NAV.SKILLS',
        canActivate: [adminManagedSkillsCanActivateGuard],
        providers: [
          provideState(fromAdmin.adminManagedSkillsFeature),
          provideEffects(fromAdmin.AdminManagedSkillsEffects),
          // Tester opens a conversation through RouterService, which reads
          // these two slices; the page loads the skills on entry. The chats
          // route registers these slices and AssistantsEffects again, which
          // keeps their state and runs the effects once.
          provideState(fromChats.chatsFeature),
          provideState(fromChatsAssistants.assistantsFeature),
          provideEffects(fromChatsAssistants.AssistantsEffects),
        ],
        loadComponent: () =>
          import('./admin/managed-skills/admin-managed-skills.page').then(
            (m) => m.AdminManagedSkillsPage,
          ),
        canDeactivate: [assistantsCanDeactivateGuard],
      },
    ],
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  { path: '**', component: NotFoundPage },
];
